import { computed, ComputedRef, unref } from 'vue';
import { CODES, useLiveQueryV2 } from '@libs/use';
import { omitBy } from '@libs/utils';
import { MaybeRef } from '@vueuse/core';

import {
  SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX,
  SYMBOL_TRADE_CHANNEL_PREFIX,
  SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX,
  SYMBOL_INDIVIDUAL_PREFIX,
  SYMBOL_QUERY_KEYS,
  VALID_SECTOR_INVESTMENT_FUNDS,
  SYMBOL_NAV_PREFIX,
} from '../constants';
import {
  transformClosingPriceChange,
  transformIndividualFirm,
  transformNav,
  transformSymbolTradingStateChange,
  transformTradePriceChange,
} from '../utils/transforms';
import {
  calcBubbleOfInvestmentFunds,
  calcIncomingIndividualMoney,
  calcIndividualDensity,
} from '../utils/calculator';
import * as API from '../requests';

export type IndustryGroupsOptions = {
  sectorCodes?: string | Array<string | number>;
  types?: string | Array<string | number>;
  subSectorCodes?: string | Array<string | number> | null;
};

export default function useLiveIndustryGroupsQuery(
  options: ComputedRef<IndustryGroupsOptions>,
  { immediate = true }: { immediate: MaybeRef<boolean> }
) {
  const {
    result,
    execute: getIndustryGroupItems,
    isPending: isLoadingIndustryGroupsItems,
    isSuccess: isSuccessIndustryGroupsItems,
    isError: isErrorIndustryGroupsItems,
    status: industryGroupItemsStatus,
  } = useLiveQueryV2({
    fetcher: () => API.getClassificationSymbolsItems(unref(options)),
    key: SYMBOL_QUERY_KEYS.industryGroups(options),
    immediate,
    getChannels: (result) => {
      if (result?.data.result) {
        return result.data.result.reduce(
          (symbolsChannels: string[], symbol) => {
            symbolsChannels.push(
              `${SYMBOL_TRADE_CHANNEL_PREFIX}${symbol.isin}`,
              `${SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX}${symbol.isin}`,
              `${SYMBOL_INDIVIDUAL_PREFIX}${symbol.isin}`,
              `${SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX}${symbol.isin}`,
              `${SYMBOL_NAV_PREFIX}${symbol.isin}`
            );

            return symbolsChannels;
          },
          []
        );
      }

      return [];
    },
    onUpdate: (signal) => {
      const actions = {
        [CODES.SymbolTradePriceChangeSignal]: transformTradePriceChange,
        [CODES.TseInstrumentClosingPriceChangeSignal]:
          transformClosingPriceChange,
        [CODES.MdpIndividualSignal]: transformIndividualFirm,
        [CODES.SymbolStateChangeSignal]: transformSymbolTradingStateChange,
        [CODES.MdpNavSignal]: transformNav,
      };

      if (actions[signal.code]) {
        const data = omitBy(
          actions[signal.code](signal.data),
          (v) => v === null || v === undefined
        );

        if (!result.value?.data?.result) {
          return;
        }

        const index = result.value.data.result.findIndex((s) => {
          return s.isin === data.isin;
        });

        if (
          signal.code === CODES.SymbolTradePriceChangeSignal &&
          result.value?.data.result[index] !== data.lastTradePrice
        ) {
          Object.assign(result.value?.data.result[index], data);
        } else if (
          signal.code === CODES.TseInstrumentClosingPriceChangeSignal &&
          result.value?.data.result[index] !== data.closingPrice
        ) {
          Object.assign(result.value?.data.result[index], data);
        } else if (signal.code === CODES.MdpIndividualSignal) {
          Object.assign(result.value?.data.result[index], data);
        } else if (signal.code === CODES.SymbolStateChangeSignal) {
          Object.assign(result.value?.data, data);
        } else if (signal.code === CODES.MdpNavSignal) {
          Object.assign(result.value?.data.result[index], data);
        }
      }
    },
  });

  const bubbleValueCalculation = (row) => {
    return row.sectorCode &&
      VALID_SECTOR_INVESTMENT_FUNDS.includes(row.sectorCode)
      ? calcBubbleOfInvestmentFunds({
          lastTradePrice: row.lastTradePrice as number,
          priceOfNav: row.priceOfRedemptionNav as number,
        })
      : 0;
  };

  const industryGroupsItems = computed(() => {
    if (!result.value?.data.result?.length) {
      return [];
    }

    return result.value?.data.result?.map((symbol) => {
      const individualBuyDensity = calcIndividualDensity({
        individualCount: symbol.individualBuyCount,
        individualVolume: symbol.individualBuyVolume,
        closingPrice: symbol.closingPrice,
      });
      const individualSellDensity = calcIndividualDensity({
        individualCount: symbol.individualSellCount,
        individualVolume: symbol.individualSellVolume,
        closingPrice: symbol.closingPrice,
      });
      const bubbleValue = bubbleValueCalculation(symbol);

      return {
        ...symbol,
        individualBuyDensity,
        individualSellDensity,
        bubbleValue,
        incomingIndividualMoney: calcIncomingIndividualMoney({
          buyIndividualVolume: symbol.individualBuyVolume,
          sellIndividualVolume: symbol.individualSellVolume,
          closingPrice: symbol.closingPrice,
        }),
      };
    });
  });

  return {
    industryGroupsItems,
    getIndustryGroupItems,
    isErrorIndustryGroupsItems,
    isLoadingIndustryGroupsItems,
    isSuccessIndustryGroupsItems,
    industryGroupItemsStatus,
  };
}
